import type { ChamadoKanban } from "../types/api";

/**
 * Normaliza status dos chamados para as 3 colunas do kanban
 */
export const normalizeStatus = (status: string): string => {
  if (status === "Solicitado") return "Solicitados";
  if (status === "Finalizado pela T4i" || status === "Concluído")
    return "Concluídos";
  // Agrupa todos os outros status (Em Análise, Em Desenvolvimento, Aguardando Teste, etc.) como "Em andamento"
  return "Em andamento";
};

/**
 * Agrupa chamados por status normalizado e aplica ordenação específica por coluna:
 * - "Solicitados": Por data de criação descendente (mais recente primeiro)
 * - "Em andamento": Por tempo restante, priorizando SLA ativo sobre pausados
 * - "Concluídos": Por data de conclusão descendente (mais recente primeiro)
 */
export const groupChamadosByStatus = (
  chamados: ChamadoKanban[],
): Record<string, ChamadoKanban[]> => {
  const grouped = chamados.reduce(
    (acc: Record<string, ChamadoKanban[]>, chamado: ChamadoKanban) => {
      const status = normalizeStatus(chamado.status.nome);
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(chamado);
      return acc;
    },
    {},
  );

  // Ordenar cada grupo por tempo restante e depois severidade
  Object.keys(grouped).forEach((status) => {
    grouped[status].sort((a, b) => {
      const aNovas = ehNovaFuncionalidade(a.categoria);
      const bNovas = ehNovaFuncionalidade(b.categoria);

      if (aNovas && bNovas) {
        const prioA = a.prioridade ?? Number.MAX_SAFE_INTEGER;
        const prioB = b.prioridade ?? Number.MAX_SAFE_INTEGER;
        if (prioA !== prioB) {
          return prioA - prioB;
        }

        const dataA = new Date(a.dataCriacao).getTime();
        const dataB = new Date(b.dataCriacao).getTime();
        return dataB - dataA;
      }

      // Para coluna "Concluídos": ordenar APENAS por data de conclusão desc (mais recente primeiro)
      if (status === "Concluídos") {
        const dataConclusaoA = a.dataConclusao
          ? new Date(a.dataConclusao).getTime()
          : 0;
        const dataConclusaoB = b.dataConclusao
          ? new Date(b.dataConclusao).getTime()
          : 0;
        return dataConclusaoB - dataConclusaoA; // desc: mais recente primeiro
      }

      // 1. SLA vencido primeiro (sempre no topo) - para outras colunas
      if (a.slaInfo?.slaVencido && !b.slaInfo?.slaVencido) return -1;
      if (!a.slaInfo?.slaVencido && b.slaInfo?.slaVencido) return 1;

      // 2. Para coluna "Solicitados": ordenar por data de criação desc (mais recente primeiro)
      if (status === "Solicitados") {
        const dataA = new Date(a.dataCriacao).getTime();
        const dataB = new Date(b.dataCriacao).getTime();
        return dataB - dataA; // desc: mais recente primeiro
      }

      // 3. Para coluna "Em andamento": Vencido > Ativo (por tempo) > Pausado (por tempo) > Na Fila
      if (status === "Em andamento") {
        const aNaFila = isNaFilaSLA(a.slaInfo?.statusSla);
        const bNaFila = isNaFilaSLA(b.slaInfo?.statusSla);
        const aPausado =
          !aNaFila &&
          (isSLAPausado(a.status.nome) ||
            a.slaInfo?.statusSla?.toLowerCase().includes("pausado"));
        const bPausado =
          !bNaFila &&
          (isSLAPausado(b.status.nome) ||
            b.slaInfo?.statusSla?.toLowerCase().includes("pausado"));

        // Classificar em grupos: ativo=0, pausado=1, na fila=2
        const grupoA = aNaFila ? 2 : aPausado ? 1 : 0;
        const grupoB = bNaFila ? 2 : bPausado ? 1 : 0;

        if (grupoA !== grupoB) return grupoA - grupoB;

        // Dentro do mesmo grupo: ordenar por tempo restante (menor primeiro)
        const tempoA = parseTempoRestanteToSeconds(
          a.slaInfo?.tempoRestanteConclusao || "",
        );
        const tempoB = parseTempoRestanteToSeconds(
          b.slaInfo?.tempoRestanteConclusao || "",
        );

        if (tempoA > 0 && tempoB > 0) return tempoA - tempoB;
        if (tempoA > 0 && tempoB <= 0) return -1;
        if (tempoA <= 0 && tempoB > 0) return 1;

        // Desempate por severidade
        const sevA =
          a.slaInfo?.severidadeAtendimento?.numeroSeveridade || a.severidade;
        const sevB =
          b.slaInfo?.severidadeAtendimento?.numeroSeveridade || b.severidade;
        return sevA - sevB;
      }

      // 4. Por tempo restante (menor tempo primeiro) - para outras colunas
      const tempoA = parseTempoRestanteToSeconds(
        a.slaInfo?.tempoRestanteConclusao || "",
      );
      const tempoB = parseTempoRestanteToSeconds(
        b.slaInfo?.tempoRestanteConclusao || "",
      );

      if (tempoA > 0 && tempoB > 0) return tempoA - tempoB;
      if (tempoA > 0 && tempoB <= 0) return -1;
      if (tempoA <= 0 && tempoB > 0) return 1;

      // 5. Se tempos são iguais ou ambos sem tempo, ordena por severidade
      const severidadeA =
        a.slaInfo?.severidadeAtendimento?.numeroSeveridade || a.severidade;
      const severidadeB =
        b.slaInfo?.severidadeAtendimento?.numeroSeveridade || b.severidade;

      return severidadeA - severidadeB;
    });
  });

  return grouped;
};

/**
 * Formatar tempo restante para formato amigável
 * Converte formatos como "3.21:48:21.3278630" para "XX horas YY minutos"
 */
export const formatTempoRestante = (tempoRestante: string): string => {
  if (!tempoRestante) return "";

  try {
    let totalHoras = 0;
    let minutos = 0;

    // Formato: "3.21:48:21.3278630" (dias.horas:minutos:segundos.milissegundos) ou "21:48:21.3278630" (horas:minutos:segundos.milissegundos)
    if (tempoRestante.includes(".") && tempoRestante.includes(":")) {
      const partes = tempoRestante.split(".");
      let dias = 0;
      let tempoStr: string;

      if (partes[0].includes(":")) {
        // Formato: horas:minutos:segundos.milissegundos
        tempoStr = partes[0];
      } else {
        // Formato: dias.horas:minutos:segundos.milissegundos
        dias = parseInt(partes[0]) || 0;
        tempoStr = partes[1];
      }

      // Pega a parte do tempo (horas:minutos:segundos)
      const tempoPartes = tempoStr.split(":");
      const horas = parseInt(tempoPartes[0]) || 0;
      minutos = parseInt(tempoPartes[1]) || 0;

      // Converte dias para horas e soma com as horas existentes
      totalHoras = dias * 24 + horas;
    }
    // Formato PT (ISO 8601): "PT2H30M" ou similar
    else if (tempoRestante.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)) {
      const matchISO = tempoRestante.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
      if (matchISO) {
        totalHoras = parseInt(matchISO[1]) || 0;
        minutos = parseInt(matchISO[2]) || 0;
      }
    }
    // Formato simples "Xh Ymin"
    else if (tempoRestante.includes("h") || tempoRestante.includes("min")) {
      const horasMatch = tempoRestante.match(/(\d+)h/);
      const minutosMatch = tempoRestante.match(/(\d+)min/);
      totalHoras = horasMatch ? parseInt(horasMatch[1]) : 0;
      minutos = minutosMatch ? parseInt(minutosMatch[1]) : 0;
    }
    // Formato "HH:MM:SS" ou "HH:MM"
    else if (tempoRestante.includes(":")) {
      const partes = tempoRestante.split(":");
      if (partes.length >= 2) {
        totalHoras = parseInt(partes[0]) || 0;
        minutos = parseInt(partes[1]) || 0;
      }
    }
    // Formato apenas numérico (assumindo minutos)
    else if (!isNaN(parseInt(tempoRestante))) {
      const totalMinutos = parseInt(tempoRestante);
      totalHoras = Math.floor(totalMinutos / 60);
      minutos = totalMinutos % 60;
    }

    // Formatar no padrão "XX horas YY minutos"
    const partes = [];

    if (totalHoras > 0) {
      partes.push(`${totalHoras} ${totalHoras === 1 ? "hora" : "horas"}`);
    }

    if (minutos > 0) {
      partes.push(`${minutos} ${minutos === 1 ? "minuto" : "minutos"}`);
    }

    return partes.join(" ") || "0 minutos";
  } catch (e) {
    console.warn("Erro ao formatar tempo restante:", tempoRestante, e);
    return tempoRestante;
  }
};

/**
 * Converte string de tempo restante para total de segundos
 * Usado para criar countdown em tempo real
 */
export const parseTempoRestanteToSeconds = (tempoRestante: string): number => {
  if (!tempoRestante) return 0;

  try {
    // Formato: "HH:MM:SS.milissegundos" (como "04:27:19.1861787")
    if (tempoRestante.includes(":")) {
      // Remove milissegundos se existir
      const tempoSemMilis = tempoRestante.split(".")[0];
      const partes = tempoSemMilis.split(":");

      if (partes.length >= 3) {
        // Formato HH:MM:SS
        const horas = parseInt(partes[0]) || 0;
        const minutos = parseInt(partes[1]) || 0;
        const segundos = parseInt(partes[2]) || 0;

        return horas * 3600 + minutos * 60 + segundos;
      } else if (partes.length === 2) {
        // Formato MM:SS
        const minutos = parseInt(partes[0]) || 0;
        const segundos = parseInt(partes[1]) || 0;

        return minutos * 60 + segundos;
      }
    }

    // Formato com dias: "3.21:48:21.3278630" (dias.horas:minutos:segundos.milissegundos)
    if (tempoRestante.includes(".") && tempoRestante.includes(":")) {
      const partes = tempoRestante.split(".");

      // Verifica se o primeiro elemento é um número simples (dias)
      if (partes[0].length <= 3 && !partes[0].includes(":")) {
        const dias = parseInt(partes[0]) || 0;

        // Pega a parte do tempo (horas:minutos:segundos)
        const tempoPartes = partes[1].split(":");
        const horas = parseInt(tempoPartes[0]) || 0;
        const minutos = parseInt(tempoPartes[1]) || 0;
        const segundos = parseInt(tempoPartes[2]) || 0;

        return dias * 86400 + horas * 3600 + minutos * 60 + segundos;
      }
    }

    // Formato PT (ISO 8601): "PT2H30M15S"
    const matchISO = tempoRestante.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (matchISO) {
      const horas = parseInt(matchISO[1]) || 0;
      const minutos = parseInt(matchISO[2]) || 0;
      const segundos = parseInt(matchISO[3]) || 0;

      return horas * 3600 + minutos * 60 + segundos;
    }
  } catch (e) {
    console.warn("Erro ao converter tempo para segundos:", tempoRestante, e);
  }

  return 0;
};

/**
 * Formatar segundos para formato HH:MM:SS ou com dias se necessário
 */
export const formatSecondsToTime = (
  totalSeconds: number,
  includeSeconds: boolean = true,
): string => {
  if (totalSeconds <= 0) return includeSeconds ? "00:00:00" : "Vencido";

  const dias = Math.floor(totalSeconds / 86400);
  const horas = Math.floor((totalSeconds % 86400) / 3600);
  const minutos = Math.floor((totalSeconds % 3600) / 60);
  const segundos = totalSeconds % 60;

  const resultado = [];

  if (dias > 0) {
    resultado.push(`${dias}d`);
  }

  const horasFormatadas = String(horas).padStart(2, "0");
  const minutosFormatados = String(minutos).padStart(2, "0");

  if (includeSeconds) {
    const segundosFormatados = String(segundos).padStart(2, "0");
    resultado.push(
      `${horasFormatadas}:${minutosFormatados}:${segundosFormatados}`,
    );
  } else {
    resultado.push(`${horasFormatadas}:${minutosFormatados}`);
  }

  return resultado.join(" ");
};

/**
 * Formatar segundos para formato "X horas Y minutos"
 */
export const formatSecondsToHumanTime = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return "Vencido";

  // Converte tudo para horas e minutos (sem mostrar dias separadamente)
  const totalHoras = Math.floor(totalSeconds / 3600);
  const minutos = Math.floor((totalSeconds % 3600) / 60);

  const partes = [];

  if (totalHoras > 0) {
    partes.push(`${totalHoras} ${totalHoras === 1 ? "hora" : "horas"}`);
  }

  if (minutos > 0) {
    partes.push(`${minutos} ${minutos === 1 ? "minuto" : "minutos"}`);
  }

  // Se não tem nenhuma parte significativa, mostra apenas "menos de 1 minuto"
  if (partes.length === 0) {
    return "menos de 1 minuto";
  }

  return partes.join(" ");
};

/**
 * Verificar se SLA está pausado baseado no status do chamado.
 * Nota: "Aguardando na Fila" da fila SLA NÃO é pausado — é um chamado
 * cujo SLA ainda não iniciou. Essa distinção é feita via statusSla da API.
 */
export const isSLAPausado = (status: string): boolean => {
  const statusLower = status.toLowerCase();
  return (
    statusLower.includes("pausado") ||
    statusLower.includes("aguardando") ||
    statusLower.includes("cliente") ||
    statusLower.includes("pendente")
  );
};

/**
 * Verificar se o chamado está aguardando na fila SLA (SLA não iniciou).
 */
export const isNaFilaSLA = (statusSla?: string): boolean => {
  if (!statusSla) return false;
  return statusSla.toLowerCase().includes("aguardando na fila");
};

/**
 * Obter label da severidade
 */
export const getSeveridadeLabel = (severidade: number): string => {
  switch (severidade) {
    case 1:
      return "Crítica";
    case 2:
      return "Alta";
    case 3:
      return "Média";
    case 4:
      return "Baixa";
    default:
      return "Não definida";
  }
};

/**
 * Obter classes CSS para status do chamado
 */
export const getStatusClasses = (status: string): string => {
  const statusLower = status.toLowerCase();

  if (statusLower.includes("solicitado")) {
    return "bg-blue-100 text-blue-800 border-blue-200";
  } else if (
    statusLower.includes("análise") ||
    statusLower.includes("desenvolvimento") ||
    statusLower.includes("andamento")
  ) {
    return "bg-yellow-100 text-yellow-800 border-yellow-200";
  } else if (
    statusLower.includes("finalizado") ||
    statusLower.includes("concluído")
  ) {
    return "bg-green-100 text-green-800 border-green-200";
  } else if (
    statusLower.includes("aguardando") ||
    statusLower.includes("teste")
  ) {
    return "bg-orange-100 text-orange-800 border-orange-200";
  } else {
    return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

/**
 * Configuração das colunas do kanban
 */
export const getStatusConfig = () => ({
  Solicitados: { color: "blue", title: "Solicitados" },
  "Em andamento": { color: "yellow", title: "Em andamento" },
  Concluídos: { color: "green", title: "Concluídos" },
});

/**
 * Calcular estatísticas dos chamados
 */
export const calculateChamadosStats = (chamados: ChamadoKanban[]) => {
  const total = chamados.length;
  const emAndamento = chamados.filter(
    (c) => normalizeStatus(c.status.nome) === "Em andamento",
  ).length;
  const concluidos = chamados.filter(
    (c) => normalizeStatus(c.status.nome) === "Concluídos",
  ).length;
  const vencidos = chamados.filter((c) => c.slaInfo?.slaVencido).length;

  return {
    total,
    emAndamento,
    concluidos,
    vencidos,
    percentualConclusao: total > 0 ? Math.round((concluidos / total) * 100) : 0,
  };
};

/**
 * A categoria de novas funcionalidades, que não entra na fila de SLA. Pelo começo do nome: ela se
 * chamava "Novas Funcionalidades / Melhorias" e passou a "Novas Funcionalidades" (06/10/2026); o
 * cartão só traz o nome, e a API e o banco a reconhecem pelo id (2).
 */
export const ehNovaFuncionalidade = (categoria: string | null | undefined): boolean =>
  /^\s*novas funcionalidades/i.test(categoria ?? "");
